import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import type { BundleDefinition } from "@p0security/shared/types/bundle";
import type { BundleItem } from "@p0security/shared/types/bundle";
import type { RequestorRule } from "@p0security/shared/types/policy/types";
import {
  Button,
  type ColumnsType,
  Empty,
  type FilterValue,
  Flex,
  Modal,
  Popover,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { AllIntegrationLogos } from "components/Integrations/IntegrationLogo";
import { NamePreview } from "components/Jit/Policy/components/previews/NamePreview";
import { RequestorPreview } from "components/Jit/Policy/components/previews/RequestorPreview";
import { useInstalledIntegrations } from "components/Jit/Policy/providers/InstalledIntegrationsProvider";
import { ApprovalSummary } from "components/Jit/Policy/views/common";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { colors } from "styles/colors";
import type { UnifiedDoc } from "types/data";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import { BundlePreviewDrawer } from "./BundlePreviewDrawer";
import { CellAction, StyledTableContainer } from "./common/TablePrimitives";
import { buildTextFilterColumnProps } from "./common/tableFilterColumns";

const defaultRequestor: RequestorRule = { type: "any" };

const BundleResourcesInfo: React.FC<{ items: BundleItem[] }> = ({ items }) => {
  const { integrationMap } = useInstalledIntegrations();
  return (
    <Flex align="center" gap={8}>
      <Typography.Text>
        {items.length} {pluralize("resource", items.length)}
      </Typography.Text>
      {items.length > 0 && (
        <Popover
          content={
            <Flex vertical gap={4} className="max-w-[350px]">
              {items.map((item, ix) => {
                const label =
                  integrationMap.find((i) => i.key === item.type)?.label ??
                  capitalize(item.type);
                return (
                  <Flex align="center" key={ix} gap={8}>
                    {isKeyOf(AllIntegrationLogos, item.type) &&
                      AllIntegrationLogos[item.type]}
                    <Typography.Text>
                      {label}
                      {item.access && item.access !== "any"
                        ? `: ${capitalize(pluralize(item.access))}`
                        : ""}
                    </Typography.Text>
                  </Flex>
                );
              })}
            </Flex>
          }
          trigger="hover"
        >
          <InfoCircleOutlined
            className="cursor-pointer"
            /* eslint-disable-line react/forbid-component-props */ style={{
              color: colors.status.info.light,
            }}
          />
        </Popover>
      )}
    </Flex>
  );
};

export const BundleList: React.FC<{
  canSubmit: boolean;
  list: { docs: UnifiedDoc<BundleDefinition>[]; loading: boolean };
  remove: (id: string) => Promise<void>;
  setDisabled: (id: string, disabled: boolean) => Promise<void>;
}> = ({ canSubmit, list, remove, setDisabled }) => {
  const navigateWithSearchParams = useNavigateWithSearchParams();

  const [previewBundle, setPreviewBundle] = useState<BundleDefinition | null>(
    null
  );
  const [disablingId, setDisablingId] = useState<string | undefined>(undefined);

  const handleToggleDisabled = useCallback(
    async (id: string, checked: boolean) => {
      setDisablingId(id);
      await setDisabled(id, !checked);
      setDisablingId(undefined);
    },
    [setDisabled]
  );

  const handleDelete = useCallback(
    (record: UnifiedDoc<BundleDefinition>) => {
      Modal.confirm({
        title: "Are you sure you want to delete this bundle?",
        content: "This action cannot be undone.",
        okText: "Yes, delete bundle",
        okType: "danger",
        onOk: async () => {
          await remove(record.id);
          message.success("Bundle deleted");
        },
      });
    },
    [remove]
  );

  const handleRowClick = useCallback(
    (record: UnifiedDoc<BundleDefinition>) => ({
      onClick: () => {
        if (canSubmit) {
          navigateWithSearchParams(`bundles/edit/${record.data.id}`);
        } else {
          setPreviewBundle(record.data);
        }
      },
    }),
    [canSubmit, navigateWithSearchParams]
  );

  const handleClosePreview = useCallback(() => setPreviewBundle(null), []);

  const [filterQuery, setFilterQuery] = useQueryParams({
    name: withDefault(StringParam, null),
    identity: withDefault(StringParam, null),
    actions: withDefault(StringParam, null),
  });

  const setters = useMemo(() => {
    const setTextFilter =
      (key: "actions" | "identity" | "name") => (value: string | null) =>
        setFilterQuery({ [key]: value }, "replaceIn");
    return {
      name: setTextFilter("name"),
      identity: setTextFilter("identity"),
      actions: setTextFilter("actions"),
    };
  }, [setFilterQuery]);

  const handleFilterChange = useCallback(
    (_pagination: unknown, filters: Record<string, FilterValue | null>) => {
      setFilterQuery(
        {
          name: (filters.name?.[0] as string | undefined) ?? null,
          identity: (filters.identity?.[0] as string | undefined) ?? null,
          actions: (filters.approval?.[0] as string | undefined) ?? null,
        },
        "replaceIn"
      );
    },
    [setFilterQuery]
  );

  ////////////////////////////////////////////////////////////////////
  // TODO: This code is largely copy-paste from PolicyTableView.tsx //
  //                                                                //
  // DRY for reusability. In the meantime you MUST make coordinated //
  // changes in order to preserve UI consistency!                   //
  ////////////////////////////////////////////////////////////////////
  const columns: ColumnsType<UnifiedDoc<BundleDefinition>> = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        ...buildTextFilterColumnProps<UnifiedDoc<BundleDefinition>>({
          value: filterQuery.name,
          onChange: setters.name,
          onFilter: (value, record) => {
            const term = String(value).toLowerCase();
            return (
              record.data.label.toLowerCase().includes(term) ||
              record.data.description.toLowerCase().includes(term)
            );
          },
        }),
        render: (_, record) => (
          <div className="min-w-40">
            <NamePreview
              name={record.data.label}
              emptyLabel="(Unnamed bundle)"
              subtitle="Access Bundle"
            />
          </div>
        ),
      },
      {
        title: "Identity",
        key: "identity",
        ...buildTextFilterColumnProps<UnifiedDoc<BundleDefinition>>({
          value: filterQuery.identity,
          onChange: setters.identity,
          onFilter: (value, record) => {
            const term = String(value).toLowerCase();
            return JSON.stringify(record.data.requestor ?? defaultRequestor)
              .toLowerCase()
              .includes(term);
          },
        }),
        render: (_, record) => (
          <div className="min-w-70">
            <RequestorPreview
              requestor={record.data.requestor ?? defaultRequestor}
            />
          </div>
        ),
      },
      {
        title: "Resources",
        key: "members",
        render: (_, record) => (
          <BundleResourcesInfo items={record.data.items} />
        ),
      },
      {
        title: "Actions",
        key: "approval",
        ...buildTextFilterColumnProps<UnifiedDoc<BundleDefinition>>({
          value: filterQuery.actions,
          onChange: setters.actions,
          onFilter: (value, record) => {
            const term = String(value).toLowerCase();
            return JSON.stringify(record.data.approval ?? [])
              .toLowerCase()
              .includes(term);
          },
        }),
        render: (_, record) => (
          <ApprovalSummary record={record.data.approval} />
        ),
      },
      {
        title: "Enabled",
        key: "enabled",
        render: (_, record) => (
          <CellAction>
            <Tooltip title="Toggle access bundle. Disabled bundles are not evaluated as part of requests.">
              <Switch
                value={!record.data.disabled}
                // eslint-disable-next-line react/jsx-no-bind
                onChange={(checked) => handleToggleDisabled(record.id, checked)}
                disabled={disablingId !== undefined}
                loading={disablingId === record.id}
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Tooltip>
          </CellAction>
        ),
      },
      {
        title: "",
        key: "actions",
        render: (_, record) =>
          canSubmit ? (
            <CellAction>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={() => handleDelete(record)}
              />
            </CellAction>
          ) : null,
      },
    ],
    [
      handleToggleDisabled,
      handleDelete,
      canSubmit,
      disablingId,
      filterQuery,
      setters,
    ]
  );

  const visibleColumns = useMemo(
    () => (canSubmit ? columns : columns.filter((c) => c.key !== "actions")),
    [columns, canSubmit]
  );

  if (!list.loading && list.docs.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No access bundles yet."
      />
    );
  }

  return (
    <>
      <StyledTableContainer>
        <Table
          columns={visibleColumns}
          dataSource={list.docs}
          loading={list.loading}
          rowKey="id"
          pagination={false}
          onRow={handleRowClick}
          onChange={handleFilterChange}
        />
      </StyledTableContainer>
      <BundlePreviewDrawer
        bundle={previewBundle}
        onClose={handleClosePreview}
      />
    </>
  );
};
